import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ChevronDown, Flame, ArrowRight, Handshake, Quote, Volume2, VolumeX, Play, Clock, MapPin, Bus, Users, Accessibility } from "lucide-react";
import { FactRow } from "@/pages/Events";
import api from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { TITLE_ENSEMBLE, HERO_POSTER, MOUNTAIN_BG, MOUNTAIN_NIGHT, ALM_IMG, STUBE_IMG, SHOW_IMAGES, GALLERY, VIDEO_HERO, VIDEO_HERO_MOBILE, VIDEO_CINEMATIC, VIDEO_KRAMPUSSE, VIDEO_KRAMPUSSE_V2, TICKET_SHOP_URL } from "@/lib/media";
import Reveal, { Kicker } from "@/components/Reveal";
import MarqueeStrip from "@/components/MarqueeStrip";
import NewsletterForm from "@/components/NewsletterForm";
import Countdown from "@/components/Countdown";
import Seo from "@/components/Seo";
import SponsorsMarquee from "@/components/SponsorsMarquee";
import { Button } from "@/components/ui/button";

const ACT_KEYS = [
  { key: "horde", img: SHOW_IMAGES.horde, span: "md:col-span-7", h: "h-[420px] md:h-[520px]" },
  { key: "devil", img: SHOW_IMAGES.devil, span: "md:col-span-5", h: "h-[420px] md:h-[520px]" },
  { key: "angel", img: SHOW_IMAGES.angel, span: "md:col-span-4", h: "h-[360px]" },
  { key: "pyro", img: SHOW_IMAGES.pyro, span: "md:col-span-4", h: "h-[360px]" },
  { key: "flying", img: SHOW_IMAGES.flying, span: "md:col-span-4", h: "h-[360px]" },
  { key: "stage", img: SHOW_IMAGES.stage, span: "md:col-span-5", h: "h-[420px]" },
  { key: "mask", img: SHOW_IMAGES.mask, span: "md:col-span-7", h: "h-[420px]" },
  { key: "wagner", img: SHOW_IMAGES.wagner, span: "md:col-span-12", h: "h-[380px]" },
];

function Hero() {
  const { t, lang } = useLang();
  const ref = useRef(null);
  const [videoOn, setVideoOn] = useState(false);
  const [reducedMotion] = useState(() =>
    window.matchMedia("(prefers-reduced-motion: reduce)").matches || navigator.connection?.saveData === true
  );
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "25%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section ref={ref} data-testid="hero" className="relative min-h-[100svh] overflow-hidden">
      <motion.div style={{ y: bgY }} className="absolute inset-0">
        <img src={HERO_POSTER} alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover" />
        <video
          data-testid="hero-video"
          className={`absolute inset-0 w-full h-[130%] object-cover transition-opacity duration-1000 ${videoOn ? "opacity-100" : "opacity-0"}`}
          onPlaying={() => setVideoOn(true)}
          autoPlay={!reducedMotion}
          muted
          loop
          playsInline
          preload={reducedMotion ? "none" : "metadata"}
        >
          <source src={VIDEO_HERO_MOBILE} type="video/mp4" media="(max-width: 767px)" />
          <source src={VIDEO_HERO} type="video/mp4" />
        </video>
        <div className="absolute inset-0 bg-gradient-to-b from-hell-black/55 via-hell-black/15 to-hell-black/90" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_0%,rgba(5,5,5,0.4)_100%)]" />
      </motion.div>

      <motion.div className="relative z-10 h-full max-w-[1400px] mx-auto px-5 md:px-10 flex flex-col items-center justify-center text-center pt-16">
        <motion.div style={{ opacity: fade }} className="flex flex-col items-center">
        <div className="flex flex-col items-center mt-12 md:mt-16 mb-8">
          <motion.img
            src={TITLE_ENSEMBLE}
            alt="Die Latschenwirt – Krampus Show – Holt sie euch"
            data-testid="hero-ensemble"
            initial={{ opacity: 0, scale: 0.92, filter: "blur(10px)" }}
            animate={{ opacity: 1, scale: 1, filter: "blur(0px)" }}
            transition={{ delay: 0.7, duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
            className="w-[76vw] max-w-sm md:max-w-lg h-auto drop-shadow-[0_12px_40px_rgba(0,0,0,0.65)]"
          />
        </div>
        <motion.h1
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.25, duration: 0.9 }}
          className="font-display text-2xl md:text-4xl headline-gold tracking-[0.04em] uppercase mb-4"
        >
          {t("hero_h1")}
        </motion.h1>
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.4, duration: 0.9 }}
          className="font-cinzel text-[11px] md:text-xs tracking-[0.25em] uppercase text-hell-gold/90 mb-6"
        >
          {t("hero_fact")}
        </motion.p>
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.5, duration: 0.9 }}
          className="max-w-xl text-hell-bone/80 text-sm md:text-base leading-relaxed mb-8"
        >
          {t("hero_sub")}
        </motion.p>
        </motion.div>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.75, duration: 0.9 }}
          className="flex flex-wrap items-stretch justify-center gap-4"
        >
          <a href={TICKET_SHOP_URL} target="_blank" rel="noopener noreferrer" data-testid="badge-2011" className="group flex flex-col items-center gap-2">
            <span className="inline-flex items-center gap-3 rounded-full bg-hell-red group-hover:bg-hell-redhot text-hell-bone font-cinzel tracking-[0.12em] uppercase text-sm px-8 h-14 transition-all duration-300 group-hover:shadow-[0_0_30px_rgba(139,0,0,0.7)] group-hover:-translate-y-0.5">
              <span className="font-display text-2xl tracking-[0.03em]">20.11.</span>
              <span className="opacity-60">·</span>
              <span>{t("badge_fri")} — {t("badge_cta")}</span>
            </span>
          </a>
          <a href={TICKET_SHOP_URL} target="_blank" rel="noopener noreferrer" data-testid="badge-2111" className="group flex flex-col items-center gap-2">
            <span className="inline-flex items-center gap-3 rounded-full bg-hell-red group-hover:bg-hell-redhot text-hell-bone font-cinzel tracking-[0.12em] uppercase text-sm px-8 h-14 transition-all duration-300 group-hover:shadow-[0_0_30px_rgba(139,0,0,0.7)] group-hover:-translate-y-0.5">
              <span className="font-display text-2xl tracking-[0.03em]">21.11.</span>
              <span className="opacity-60">·</span>
              <span>{t("badge_sat")} — {t("badge_cta")}</span>
            </span>
          </a>
        </motion.div>
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.7, duration: 0.9 }}
          className="mt-5 flex gap-6 justify-center font-cinzel text-[10px] md:text-[11px] tracking-[0.2em] uppercase"
        >
          <Link to={`${lang === "en" ? "/en" : ""}/krampus-show-2026-11-20`} data-testid="event-link-20" className="text-hell-gold/80 hover:text-hell-gold transition-colors">
            {lang === "de" ? "Details Freitag 20.11." : "Details Friday 20 Nov"}
          </Link>
          <Link to={`${lang === "en" ? "/en" : ""}/krampus-show-2026-11-21`} data-testid="event-link-21" className="text-hell-gold/80 hover:text-hell-gold transition-colors">
            {lang === "de" ? "Details Samstag 21.11." : "Details Saturday 21 Nov"}
          </Link>
        </motion.p>
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 2.1, duration: 1 }}
          data-testid="hero-meta"
          className="mt-10 font-cinzel text-[10px] md:text-xs tracking-[0.3em] uppercase text-hell-bone/60 md:text-center"
        >
          {t("hero_meta")}
        </motion.p>
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 2.3, duration: 1 }}
          data-testid="hero-urgency"
          className="mt-3 flex items-center justify-center gap-2 font-cinzel text-[10px] md:text-xs tracking-[0.3em] uppercase text-hell-ember animate-flicker"
        >
          <Flame className="w-3.5 h-3.5" />
          {t("hero_urgency")}
        </motion.p>
        <Countdown />
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 2.3 }}
          className="absolute bottom-6 left-1/2 -translate-x-1/2 hidden md:block"
        >
          <ChevronDown className="w-6 h-6 text-hell-gold animate-bounce" />
        </motion.div>
      </motion.div>
    </section>
  );
}

function Acts() {
  const { t } = useLang();
  return (
    <section id="show" data-testid="acts-section" className="max-w-[1400px] mx-auto px-5 md:px-10 py-24 md:py-36">
      <Reveal className="md:text-center">
        <Kicker>{t("acts_kicker")}</Kicker>
        <h2 className="font-display text-5xl md:text-7xl headline-gold mt-4 mb-16">{t("acts_title")}</h2>
      </Reveal>
      <div className="grid grid-cols-1 md:grid-cols-12 gap-5">
        {ACT_KEYS.map((act, i) => (
          <motion.figure
            key={act.key}
            data-testid={`act-${act.key}`}
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.8, delay: (i % 3) * 0.1, ease: [0.22, 1, 0.36, 1] }}
            className={`group relative overflow-hidden border border-white/10 ${act.span} ${act.h}`}
          >
            <img src={act.img} alt={t(`act_${act.key}_t`)} loading="lazy"
              className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1.2s] group-hover:scale-110" />
            <div className="absolute inset-0 bg-gradient-to-t from-hell-black/95 via-hell-black/30 to-transparent" />
            <figcaption className="absolute bottom-0 inset-x-0 p-6 md:p-8">
              <span className="font-cinzel text-[10px] tracking-[0.3em] uppercase text-hell-ember">0{i + 1}</span>
              <h3 className="font-display text-2xl md:text-3xl text-hell-bone mt-1 mb-2">{t(`act_${act.key}_t`)}</h3>
              <p className="text-sm text-hell-bone/70 max-w-md opacity-0 translate-y-3 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-500">
                {t(`act_${act.key}_d`)}
              </p>
            </figcaption>
          </motion.figure>
        ))}
      </div>
    </section>
  );
}

function Mountain() {
  const { t } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-10%", "10%"]);

  return (
    <section ref={ref} data-testid="mountain-section" className="relative overflow-hidden py-32 md:py-44">
      <motion.div style={{ y }} className="absolute inset-0">
        <img src={MOUNTAIN_BG} alt="Untersberg" className="w-full h-[120%] object-cover" />
        <div className="absolute inset-0 bg-hell-black/70" />
      </motion.div>
      <div className="relative z-10 max-w-[1400px] mx-auto px-5 md:px-10 grid md:grid-cols-12 gap-10 items-center">
        <Reveal className="md:col-span-7 md:text-center">
          <Kicker>{t("mountain_kicker")}</Kicker>
          <h2 className="font-display text-5xl md:text-7xl headline-gold mt-4 mb-8 ember-glow">{t("mountain_title")}</h2>
          <p className="text-hell-bone/80 text-base md:text-lg leading-relaxed max-w-2xl md:mx-auto mb-8">{t("mountain_text")}</p>
          <a href="https://www.latschenwirt.at" target="_blank" rel="noreferrer">
            <Button data-testid="mountain-cta" variant="outline" className="rounded-full border-hell-gold/50 text-hell-gold hover:bg-hell-gold/10 font-cinzel tracking-[0.15em] uppercase text-xs px-7 h-12">
              {t("mountain_cta")}<ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </a>
        </Reveal>
        <Reveal delay={0.2} className="md:col-span-5">
          <div className="space-y-10">
            <div className="relative">
              <img src={MOUNTAIN_NIGHT} alt="Untersberg-Gipfel bei Nacht" data-testid="mountain-night-img" className="w-full h-[280px] object-cover border border-white/10" />
              <div className="absolute -bottom-5 -left-5 bg-hell-red px-5 py-3 font-cinzel text-[10px] tracking-[0.25em] uppercase text-hell-bone">
                Untersberg
              </div>
            </div>
            <div className="relative">
              <img src={STUBE_IMG} alt="Latschenwirt Stuben" data-testid="latschenwirt-img" className="w-full h-[280px] object-cover border border-white/10" />
              <div className="absolute -bottom-5 -left-5 bg-hell-red px-5 py-3 font-cinzel text-[10px] tracking-[0.25em] uppercase text-hell-bone">
                Latschenwirt
              </div>
            </div>
            <div className="relative">
              <img src={ALM_IMG} alt="Wolfschwang Alm" className="w-full h-[280px] object-cover border border-white/10" />
              <div className="absolute -bottom-5 -left-5 bg-hell-red px-5 py-3 font-cinzel text-[10px] tracking-[0.25em] uppercase text-hell-bone">
                Wolfschwang Alm
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function TicketsPreview() {
  const { t, lang } = useLang();
  const [products, setProducts] = useState([]);
  useEffect(() => {
    api.get("/products", { params: { kind: "ticket" } }).then((r) => setProducts(r.data)).catch(() => {});
  }, []);
  return (
    <section data-testid="tickets-preview" className="max-w-[1400px] mx-auto px-5 md:px-10 py-24 md:py-36">
      <Reveal className="flex flex-col items-center text-center gap-6 mb-14">
        <div>
          <Kicker>{t("tickets_kicker")}</Kicker>
          <h2 className="font-display text-5xl md:text-7xl headline-gold mt-4">{t("tickets_title")}</h2>
          <p className="text-hell-smoke mt-4 max-w-xl md:mx-auto">{t("tickets_sub")}</p>
        </div>
        <a href={TICKET_SHOP_URL} target="_blank" rel="noopener noreferrer">
          <Button data-testid="tickets-preview-cta" className="rounded-full bg-hell-red hover:bg-hell-redhot text-hell-bone font-cinzel tracking-[0.15em] uppercase text-xs px-7 h-12">
            {t("tickets_all")}<ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </a>
      </Reveal>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {products.map((p, i) => (
          <Reveal key={p.product_id} delay={i * 0.08}>
            <a href={p.contact_only ? `mailto:office@latschenwirt.at?subject=${encodeURIComponent(t(p.lookup_key === "ticket_vip_tisch" ? "mail_subj_vip_tisch" : "mail_subj_vip_sponsor"))}` : TICKET_SHOP_URL} target={p.contact_only ? undefined : "_blank"} rel="noopener noreferrer" data-testid={`ticket-teaser-${p.lookup_key}`} className="group block relative border border-white/10 bg-hell-coal overflow-hidden hover:border-hell-gold/40 transition-colors duration-500">
              <div className="h-44 overflow-hidden">
                <img src={p.image} alt={lang === "de" ? p.name_de : p.name_en} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
              </div>
              <div className="p-5">
                <h3 className="font-display text-lg text-hell-bone mb-1">{lang === "de" ? p.name_de : p.name_en}</h3>
                <p className="font-cinzel text-hell-gold text-sm">{p.contact_only ? t("on_request") : <>{p.price_prefix ? `${t("from_price")} ` : ""}€ {p.price.toLocaleString("de-AT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</>}</p>
              </div>
            </a>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function SponsorsPreview() {
  const { t } = useLang();
  const [sponsors, setSponsors] = useState([]);
  useEffect(() => {
    api.get("/sponsors").then((r) => setSponsors(r.data)).catch(() => {});
  }, []);
  return (
    <section data-testid="sponsors-preview" className="bg-hell-coal border-y border-white/10 py-24 md:py-36">
      <div className="max-w-[1400px] mx-auto px-5 md:px-10">
        <Reveal className="flex flex-col items-center text-center gap-6 mb-14">
          <div>
            <Kicker>{t("sponsors_kicker")}</Kicker>
            <h2 className="font-display text-5xl md:text-7xl headline-gold mt-4">{t("sponsors_title")}</h2>
            <p className="text-hell-smoke mt-4 max-w-xl md:mx-auto">{t("sponsors_sub")}</p>
          </div>
          <Link to="/sponsoren">
            <Button data-testid="sponsors-preview-cta" className="rounded-full bg-hell-red hover:bg-hell-redhot text-hell-bone font-cinzel tracking-[0.15em] uppercase text-xs px-7 h-12">
              <Handshake className="w-4 h-4 mr-2" />{t("sponsors_all")}
            </Button>
          </Link>
        </Reveal>
        <div className="mt-14 -mx-5 md:-mx-10">
          <SponsorsMarquee sponsors={sponsors} />
        </div>
      </div>
    </section>
  );
}

function ReelVideo({ tile, sound }) {
  const [on, setOn] = useState(false);
  const vidRef = useRef(null);
  useEffect(() => {
    const v = vidRef.current;
    if (!v) return;
    const io = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { v.play().catch(() => {}); } else { v.pause(); }
    }, { rootMargin: "120px" });
    io.observe(v);
    return () => io.disconnect();
  }, []);
  return (
    <>
      <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${tile.poster})` }} />
      <video
        ref={vidRef}
        className={`absolute inset-0 w-full h-full object-cover transition-[opacity,transform] duration-700 group-hover:scale-105 ${on ? "opacity-100" : "opacity-0"}`}
        onPlaying={() => setOn(true)}
        muted={!sound} loop playsInline preload="metadata"
      >
        <source src={tile.src} type="video/mp4" />
      </video>
    </>
  );
}

function Showreel() {
  const { t } = useLang();
  const [sound, setSound] = useState(false);
  const tiles = [
    { src: VIDEO_KRAMPUSSE_V2, poster: "/videos/reel_krampusse_v2.jpg", span: "md:col-span-12", h: "h-[50vh] md:h-[70vh]", label: "Krampusse V2" },
    { src: VIDEO_KRAMPUSSE, poster: "/videos/reel_krampusse.jpg", span: "md:col-span-6", h: "h-[40vh]", label: "Krampusse" },
    { src: VIDEO_CINEMATIC, poster: "/videos/reel_cinematic.jpg", span: "md:col-span-6", h: "h-[40vh]", label: "Cinematic" },
  ];
  return (
    <section data-testid="showreel-section" className="max-w-[1400px] mx-auto px-5 md:px-10 pb-24 md:pb-36">
      <Reveal className="flex flex-col items-center text-center gap-6 mb-12">
        <div>
          <Kicker>{t("reel_kicker")}</Kicker>
          <h2 className="font-display text-5xl md:text-7xl headline-gold mt-4">{t("reel_title")}</h2>
          <p className="text-hell-smoke mt-4 max-w-xl md:mx-auto">{t("reel_sub")}</p>
        </div>
        <button
          data-testid="showreel-sound-toggle"
          onClick={() => setSound(!sound)}
          className="flex items-center gap-2 border border-hell-gold/50 text-hell-gold rounded-full px-5 py-2.5 font-cinzel text-[11px] tracking-[0.2em] uppercase hover:bg-hell-gold/10 transition-colors"
        >
          {sound ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
          {t("reel_sound")} {sound ? "ON" : "OFF"}
        </button>
      </Reveal>
      <div className="grid grid-cols-1 md:grid-cols-12 gap-5">
        {tiles.map((tile, i) => (
          <Reveal key={tile.label} delay={i * 0.1} className={tile.span}>
            <div data-testid={`reel-${tile.label.toLowerCase().replace(/\s/g, "-")}`}
              className={`group relative overflow-hidden border border-white/10 ${tile.h}`}>
              <ReelVideo tile={tile} sound={sound} />
              <div className="absolute inset-0 bg-gradient-to-t from-hell-black/70 via-transparent to-transparent pointer-events-none" />
              <span className="absolute bottom-4 left-5 font-cinzel text-[10px] tracking-[0.3em] uppercase text-hell-bone/70">
                {tile.label}
              </span>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function ShowFilm() {
  const { t } = useLang();
  const [play, setPlay] = useState(false);
  return (
    <section data-testid="showfilm-section" className="max-w-[1400px] mx-auto px-5 md:px-10 pb-24 md:pb-36">
      <Reveal className="flex flex-col items-center text-center gap-4 mb-10">
        <Kicker>{t("film_kicker")}</Kicker>
        <h2 className="font-display text-5xl md:text-7xl headline-gold">{t("film_title")}</h2>
        <p className="text-hell-smoke max-w-xl">{t("film_sub")}</p>
      </Reveal>
      <Reveal>
        <div className="relative aspect-video overflow-hidden border border-white/10 bg-hell-black group">
          {play ? (
            <video
              data-testid="showfilm-video"
              src="/videos/aftermovie.mp4"
              className="w-full h-full object-cover"
              autoPlay
              controls
              playsInline
              preload="metadata"
            />
          ) : (
            <button data-testid="showfilm-play" onClick={() => setPlay(true)}
              className="absolute inset-0 w-full h-full cursor-pointer">
              <img src="/videos/film_poster.jpg" alt="Krampus-Show Aftermovie" loading="lazy"
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
              <div className="absolute inset-0 bg-hell-black/40 group-hover:bg-hell-black/25 transition-colors duration-500" />
              <span className="absolute inset-0 flex items-center justify-center">
                <span className="flex items-center justify-center w-20 h-20 md:w-24 md:h-24 rounded-full bg-hell-red border border-hell-redhot/70 shadow-[0_0_40px_rgba(139,0,0,0.6)] transition-transform duration-300 group-hover:scale-110">
                  <Play className="w-8 h-8 text-hell-bone ml-1" />
                </span>
              </span>
              <span className="absolute bottom-5 left-1/2 -translate-x-1/2 font-cinzel text-[10px] tracking-[0.25em] uppercase text-hell-bone/70 whitespace-nowrap px-4">
                {t("film_hint")}
              </span>
            </button>
          )}
        </div>
      </Reveal>
    </section>
  );
}


function Voices() {
  const { t, lang } = useLang();
  const [voices, setVoices] = useState([]);
  useEffect(() => {
    api.get("/voices").then((r) => setVoices(r.data)).catch(() => {});
  }, []);
  if (!voices.length) return null;
  return (
    <section data-testid="voices-section" className="max-w-[1400px] mx-auto px-5 md:px-10 py-24 md:py-36">
      <Reveal className="mb-14 md:text-center">
        <Kicker>{t("voices_kicker")}</Kicker>
        <h2 className="font-display text-5xl md:text-7xl headline-gold mt-4">{t("voices_title")}</h2>
        <p className="text-hell-smoke mt-4 max-w-xl md:mx-auto">{t("voices_sub")}</p>
      </Reveal>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {voices.map((v, i) => (
          <Reveal key={v.voice_id} delay={(i % 2) * 0.1}>
            <blockquote data-testid={`voice-${v.voice_id}`}
              className="relative border border-white/10 bg-hell-coal p-8 md:p-10 h-full hover:border-hell-gold/40 transition-colors duration-500">
              <Quote className="w-8 h-8 text-hell-red mb-6" fill="currentColor" />
              <p className="font-display text-3xl md:text-4xl text-hell-bone leading-tight mb-6">„{lang === "de" ? v.quote_de : v.quote_en}“</p>
              <footer className="font-cinzel text-xs tracking-[0.25em] uppercase text-hell-gold">— {v.author}</footer>
            </blockquote>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function AnreisePreview() {
  const { lang } = useLang();
  const de = lang === "de";
  return (
    <section data-testid="anreise-preview" className="max-w-[1400px] mx-auto px-5 md:px-10 pb-24 md:pb-32">
      <Reveal className="flex flex-col items-center text-center gap-6 mb-12">
        <Kicker>{de ? "Gut zu wissen" : "Good to know"}</Kicker>
        <h2 className="font-display text-5xl md:text-7xl headline-gold leading-tight">
          {de ? "Anreise & Besucherinfos" : "Getting there & visitor info"}
        </h2>
      </Reveal>
      <Reveal className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <FactRow icon={Clock} label={de ? "Ablauf des Abends" : "Schedule"} value={de ? "17:00 Shuttle ab Freilichtmuseum · 17:30 Einlass & Rahmenprogramm · 20:30 Showbeginn" : "5:00 pm shuttle from the museum · 5:30 pm doors & programme · 8:30 pm show"} />
        <FactRow icon={Bus} label={de ? "Gratis Shuttlebus" : "Free shuttle bus"} value={de ? "Ab 17:00 Uhr ab Freilichtmuseum Großgmain (Hasenweg 1)" : "From 5:00 pm from Grossgmain open-air museum (Hasenweg 1)"} />
        <FactRow icon={MapPin} label={de ? "Parken" : "Parking"} value={de ? "Keine Parkplätze beim Latschenwirt – bitte am Freilichtmuseum parken" : "No parking at the venue – please park at the open-air museum"} />
        <FactRow icon={MapPin} label={de ? "Veranstaltungsort" : "Venue"} value="Latschenwirt-Arena, Buchhöhstraße 122, 5084 Großgmain" />
        <FactRow icon={Users} label={de ? "Alter" : "Age"} value={de ? "Eintritt ab 16 Jahren" : "Admission from 16 years"} />
        <FactRow icon={Accessibility} label={de ? "Barrierefreiheit" : "Accessibility"} value={de ? "Die Arena ist teilweise barrierefrei" : "The arena is partially accessible"} />
      </Reveal>
      <Reveal className="text-center mt-10">
        <Link to={`${de ? "" : "/en"}/anreise`} data-testid="anreise-preview-link"
          className="font-cinzel text-[11px] tracking-[0.2em] uppercase text-hell-gold hover:underline">
          {de ? "Alle Anreise-Infos & Besucherfragen" : "All travel info & visitor questions"}
        </Link>
      </Reveal>
    </section>
  );
}

function GalleryStrip() {
  const { t } = useLang();
  return (
    <section className="grid grid-cols-2 md:grid-cols-4">
      {GALLERY.slice(0, 8).map((src, i) => (
        <div key={i} className="h-48 md:h-64 overflow-hidden border border-hell-black">
          <img src={src} alt={`${t("gallery_alt")} ${i + 1}`} loading="lazy"
            className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
        </div>
      ))}
    </section>
  );
}

function NewsletterSection() {
  const { t } = useLang();
  return (
    <section data-testid="newsletter-section" className="relative py-24 md:py-36 overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom,rgba(139,0,0,0.25),transparent_60%)]" />
      <div className="relative z-10 max-w-[900px] mx-auto px-5 md:px-10 text-center">
        <Reveal>
          <Kicker className="justify-center">{t("newsletter_kicker")}</Kicker>
          <h2 className="font-display text-5xl md:text-7xl headline-gold mt-4 mb-6">{t("newsletter_title")}</h2>
          <p className="text-hell-smoke mb-10">{t("newsletter_sub")}</p>
          <div className="flex justify-center">
            <NewsletterForm />
          </div>
        </Reveal>
      </div>
    </section>
  );
}

export default function Home() {
  const { t } = useLang();
  return (
    <main data-testid="home-page">
      <Seo path="/" title="Krampus-Show 2026 in Großgmain | Latschenwirt" titleEn="Krampus Show 2026 in Grossgmain near Salzburg | Latschenwirt" description="Erlebe die Krampus-Show beim Latschenwirt in Großgmain bei Salzburg am 20. & 21. November 2026. Infos zur Show, Arena- und VIP-Tickets." descriptionEn="Experience the Krampus Show at the Latschenwirt in Grossgmain near Salzburg on 20 & 21 November 2026. Show info, arena and VIP tickets." />
      <Hero />
      <MarqueeStrip items={t("marquee_items")} dark />
      <section className="max-w-[1400px] mx-auto px-5 md:px-10 py-24 md:py-36">
        <Reveal className="max-w-3xl md:mx-auto md:text-center">
          <Kicker>{t("intro_kicker")}</Kicker>
          <h2 className="font-display text-5xl md:text-7xl headline-gold mt-4 mb-8 leading-tight">{t("intro_title")}</h2>
          <div className="text-hell-bone/80 text-base md:text-lg leading-relaxed space-y-6">
            <p>{t("intro_text")}</p>
            <p>{t("intro_text2")}</p>
            <p>{t("intro_text3")}</p>
          </div>
        </Reveal>
      </section>
      <TicketsPreview />
      <AnreisePreview />
      <Acts />
      <Showreel />
      <ShowFilm />
      <MarqueeStrip items={t("marquee_items")} />
      <Mountain />
      <GalleryStrip />
      <Voices />
      <SponsorsPreview />
      <NewsletterSection />
    </main>
  );
}
